import { Tab, TabList, Tabs } from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import {
  AirtableIcon,
  EmbedViewIcon,
  FigmaIcon,
  GoogleDocsIcon,
  GoogleSheetsIcon,
  GoogleSlidesIcon,
  InfoIcon,
  LinkIcon,
  MiroIcon,
  YoutubeIcon,
} from '@sketchnote/icons';
import * as React from 'react';

const { useState } = React;

const DisclaimerMessage = ({ message }: { message?: string }) =>
  message ? (
    <div className="flex items-center">
      <InfoIcon className="mr-4 text-primary-400" />
      <small className="text-gray-500">{message}</small>
    </div>
  ) : (
    <span />
  );

const DisclaimerBox = React.memo(DisclaimerMessage);

const EMBED = Object.freeze({
  iconSize: 24,
  tabTitleSpacing: 'px-12 py-6',
  tabPanelStyle: { paddingLeft: 0, paddingRight: 0 },
});

const inputTypes = [
  {
    placholder: 'Enter custom URL',
    type: 'custom',
    icon: (
      <>
        <EmbedViewIcon className="mr-4 mt-6" size="1.4em" />
        <span className="text-sm">Embed</span>
      </>
    ),
  },
  { placholder: 'Enter Figma URL', type: 'figma', icon: <FigmaIcon size={EMBED.iconSize} /> },
  {
    placholder: 'Enter Youtube URL',
    type: 'youtube',
    icon: <YoutubeIcon size={EMBED.iconSize} />,
  },
  {
    placholder: 'Enter Google Sheets URL',
    type: 'google-sheets',
    icon: <GoogleSheetsIcon size={EMBED.iconSize} />,
    disclaimer: 'Google Sheets embed works only for public files',
  },
  {
    placholder: 'Enter Google Docs URL',
    type: 'google-docs',
    icon: <GoogleDocsIcon size={EMBED.iconSize} />,
    disclaimer: 'Google Docs embed works only for public files',
  },
  {
    placholder: 'Enter Google Slides URL',
    type: 'google-slides',
    icon: <GoogleSlidesIcon size={EMBED.iconSize} />,
    disclaimer: 'Google Slides embed works only for public files',
  },
  {
    placholder: 'Enter Airtable URL',
    type: 'airtable',
    icon: <AirtableIcon size={EMBED.iconSize} />,
    disclaimer: 'The Airtable view/base needs to be shared publicly to work as an embed',
  },
  { placholder: 'Enter Miro URL', type: 'miro', icon: <MiroIcon size={EMBED.iconSize} /> },
];

export const EmbedInput = ({ onSubmit }: { onSubmit: (url: string) => void }) => {
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [embedUrl, setEmbedUrl] = useState('');
  const [openSnackbar] = useSnackbar();

  const handleTabsChange = (index) => {
    setTabIndex(index);
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();
    if (isValidUrl(embedUrl)) {
      const youtubeEmbedUrl = getYoutubeEmbedUrl(embedUrl);
      if (youtubeEmbedUrl) {
        onSubmit(youtubeEmbedUrl);
        return;
      }
      const figmaEmbedUrl = getFigmaCode(embedUrl);
      if (figmaEmbedUrl) {
        onSubmit(figmaEmbedUrl);
        return;
      }
      const vimeoEmbedUrl = getVimeoUrl(embedUrl);
      if (vimeoEmbedUrl) {
        onSubmit(vimeoEmbedUrl);
        return;
      }
      const loomEmbedUrl = getLoomUrl(embedUrl);
      if (loomEmbedUrl) {
        onSubmit(loomEmbedUrl);
        return;
      }
      const airtableEmbedUrl = getAirtableUrl(embedUrl);
      if (airtableEmbedUrl) {
        onSubmit(airtableEmbedUrl);
        return;
      }
      onSubmit(embedUrl);
    } else {
      openSnackbar({
        text: 'Enter a valid URL',
        snackType: 'error',
        position: 'top-center',
      });
    }
  };

  const handleUrlChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setEmbedUrl(e.target.value);
    if (isValidUrl(e.target.value)) {
      const youtubeEmbedUrl = getYoutubeEmbedUrl(e.target.value);
      if (youtubeEmbedUrl) {
        setTabIndex(2);
        return;
      }
      const figmaEmbedUrl = getFigmaCode(e.target.value);
      if (figmaEmbedUrl) {
        setTabIndex(1);
        return;
      }
      const airtableEmbedUrl = getAirtableUrl(e.target.value);
      if (airtableEmbedUrl) {
        setTabIndex(6);
        return;
      }
    } else {
      setTabIndex(0);
    }
  };

  return (
    <div className="flex flex-col p-16 rounded-lg  text-gray-999 min-w-630">
      <h3 className="text-gray-400 mb-32 text-2xl text-center font-medium">
        What do you want to embed?
      </h3>
      <Tabs
        index={tabIndex}
        onChange={handleTabsChange}
        size="small"
        className="flex items-center min-2-630"
      >
        <TabList>
          {inputTypes.map((type) => (
            <Tab key={type.type} className={EMBED.tabTitleSpacing}>
              {type.icon}
            </Tab>
          ))}
        </TabList>
      </Tabs>
      <form onSubmit={handleOnSubmit} className="flex flex-col justify-center my-24">
        <div className="relative">
          <LinkIcon className="absolute top-14 left-10 text-gray-400 text-sm" />
          <input
            className="px-12 py-10 pl-30 text-gray-700 w-full rounded-sm mb-24 bg-gray-100 placeholder-gray-400"
            value={embedUrl}
            onChange={handleUrlChange}
            placeholder={inputTypes[tabIndex].placholder}
          />
        </div>
        <button
          type="submit"
          className=" bg-primary-500 text-white rounded-sm px-24 py-12 font-medium m-auto"
        >
          Add View
        </button>
      </form>
      <DisclaimerBox message={inputTypes[tabIndex].disclaimer} />
    </div>
  );
};

function isValidUrl(value: string) {
  return /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
    value
  );
}

function getYoutubeEmbedUrl(url: string) {
  const pattern =
    /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
  const match = url.match(pattern);
  if (match?.[1]) {
    return `https://www.youtube.com/embed/${match?.[1]}`;
  }
  return false;
}

function getFigmaCode(url: string) {
  const pattern =
    /^(?:https:\/\/)?(?:www\.)?figma\.com\/(file|proto)\/([0-9a-zA-Z]{22,128})(?:\/.*)?node-id=([^&]*)(?:&t=.*)?$/;
  const match = url.match(pattern);
  if (match?.[0] && match?.[2]) {
    return `https://www.figma.com/embed?embed_host=sketchnote.co&url=${match[0]}`;
  }
  return false;
}

function getVimeoUrl(url: string) {
  const pattern = /https:\/\/(www\.)?vimeo.com\/(\d+)($|\/)/;
  const match = url.match(pattern);
  if (match?.[2]) {
    return `https://player.vimeo.com/video/${match[2]}`;
  } else {
    return false;
  }
}

function getLoomUrl(url: string) {
  const pattern = /https:\/\/(www\.)?loom.com\/share\/([0-9a-z]{32,32})($|\/)/;
  const match = url.match(pattern);
  if (match?.[2]) {
    return `https://www.loom.com/embed/${match[2]}`;
  } else {
    return false;
  }
}

function getAirtableUrl(url: string) {
  const pattern = /(?:https:\/\/)?(?:www\.)?airtable\.com\/(.*)/;
  const match = url.match(pattern);
  if (match?.[1]) {
    return `https://airtable.com/embed/${match[1]}`;
  } else {
    return false;
  }
}
